import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Lock, Mail, User, Eye, EyeOff, KeyRound } from "lucide-react";

const homeFor = (u) => (u?.role === "admin" ? "/dashboard" : "/account");

export default function Login() {
  const [mode, setMode] = useState("login"); // login | signup | forgot | reset
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(homeFor(user));
  }, [user, navigate]);

  const reset = () => { setError(""); setPassword(""); setShowPw(false); };

  const login = async () => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    toast.success(data.role === "admin" ? "Welcome back, Executive." : "Welcome back.");
    navigate(homeFor(data));
  };

  const signup = async () => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    toast.success("Account created. Welcome aboard.");
    navigate(homeFor(data));
  };

  const forgot = async () => {
    const { data } = await api.post("/auth/forgot-password", { email });
    if (data.reset_token) {
      setToken(data.reset_token);
      setMode("reset");
      reset();
      toast.info("Reset issued — set your new password below.");
    } else {
      toast.success(data.message);
    }
  };

  const doReset = async () => {
    const { data } = await api.post("/auth/reset-password", { token, new_password: password });
    toast.success(data.message);
    setMode("login");
    reset();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login();
      else if (mode === "signup") await signup();
      else if (mode === "forgot") await forgot();
      else if (mode === "reset") await doReset();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = {
    login: "Executive Portal",
    signup: "Create Account",
    forgot: "Reset Password",
    reset: "Set New Password",
  };
  const cta = {
    login: "Sign In",
    signup: "Create Account",
    forgot: "Send Reset Link",
    reset: "Update Password",
  };

  return (
    <div className="min-h-screen royal-radial grain flex items-center justify-center px-4 relative" data-testid="login-page">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-gold transition-colors text-sm" data-testid="login-back-home">
        <ArrowLeft className="w-4 h-4" /> Return to Site
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={COMPANY.logo} alt="crest" className="w-32 h-32 object-contain mx-auto animate-float" style={{ filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.5))" }} />
          <h1 className="font-display text-2xl text-emerald-50 uppercase tracking-tight mt-6">{titles[mode]}</h1>
          <p className="text-sm text-slate-400 mt-2 font-garamond tracking-wide">Jahzara Phenix Jae Orion LLC</p>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-emerald-900/70 surface-glass p-7 space-y-4" data-testid="login-form">
          {mode === "signup" && (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Full Name</label>
              <div className="relative mt-2">
                <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" className="pl-9 surface-input border-emerald-900 text-emerald-50" placeholder="Your name" />
              </div>
            </div>
          )}

          {mode === "reset" ? (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Reset Token</label>
              <div className="relative mt-2">
                <KeyRound className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required value={token} onChange={(e) => setToken(e.target.value)} data-testid="reset-token" className="pl-9 surface-input border-emerald-900 text-emerald-50 font-mono text-xs" placeholder="Paste reset token" />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Email</label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="pl-9 surface-input border-emerald-900 text-emerald-50" placeholder="you@example.com" />
              </div>
            </div>
          )}

          {mode !== "forgot" && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-widest text-slate-400">{mode === "reset" ? "New Password" : "Password"}</label>
                {mode === "login" && (
                  <button type="button" onClick={() => { setMode("forgot"); reset(); }} data-testid="forgot-password-link" className="text-[11px] text-gold hover:underline">
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative mt-2">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  required
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="pl-9 pr-10 surface-input border-emerald-900 text-emerald-50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password"
                  aria-label="Toggle password visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-gold"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {error && <div className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}

          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-12 tracking-wide hover-emerald-glow border border-emerald-400/30">
            {loading ? "Please wait…" : cta[mode]}
          </Button>

          {mode === "login" && (
            <>
              <div className="relative py-1">
                <div className="border-t border-emerald-900/60" />
                <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 surface-solid px-3 text-[11px] text-slate-500 uppercase tracking-widest">or</span>
              </div>
              <Button type="button" onClick={googleLogin} data-testid="login-google" className="w-full bg-white hover:bg-slate-100 text-slate-800 rounded-full h-12 tracking-wide">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
                Continue with Google
              </Button>
            </>
          )}

          <div className="text-center text-[12px] text-slate-500 pt-1">
            {mode === "login" && (
              <>New here? <button type="button" onClick={() => { setMode("signup"); reset(); }} data-testid="switch-signup" className="text-gold hover:underline">Create an account</button></>
            )}
            {mode === "signup" && (
              <>Already registered? <button type="button" onClick={() => { setMode("login"); reset(); }} data-testid="switch-login" className="text-gold hover:underline">Sign in</button></>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <button type="button" onClick={() => { setMode("login"); reset(); }} data-testid="back-to-login" className="text-gold hover:underline">Back to sign in</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
